// Único lector del saldo de créditos de imágenes (ADR-2026-10-27-facturacion-por-marca).
//
// Con `company_profiles.facturacion_por_marca = false` (todas las empresas salvo las que se
// prendan a mano) lee EXACTAMENTE lo mismo que antes: la bolsa de la empresa. Con la bandera
// prendida el saldo es el de la billetera de la marca activa (`billeteras_de_marca`).
// `saldo.test.ts` tiene una guardia que impide leer esas columnas desde otro archivo.

import { supabase } from './supabase';
import { resolveMarcaActivaEstricta } from '../utils/marcaActiva';

/**
 * La marca cuya billetera paga, según un saldo: sólo con facturación por marca. `null` = paga
 * la empresa (o no se supo la marca), y quien reserva créditos cuenta los de toda la empresa.
 */
export function marcaQuePaga(saldo: Pick<Saldo, 'facturaPorMarca' | 'brandId'> | null | undefined): string | null {
  return saldo?.facturaPorMarca ? saldo.brandId : null;
}

export interface Saldo {
  /** Créditos que se pueden gastar ya (incluye el regalo/bonus, aunque no haya plan). */
  disponibles: number;
  /** Cupo mensual del plan; `null` si no hay dato. */
  capacidad: number | null;
  plan: string | null;
  facturaPorMarca: boolean;
  /** Marca que paga (sólo con facturación por marca). */
  brandId: string | null;
  /** Con facturación por marca: la marca no tiene billetera o su billetera no tiene plan. */
  sinPlanDeMarca: boolean;
}

const num = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

function faltaLaColumna(err: { code?: string; message?: string }): boolean {
  return err.code === '42703' || /column .* does not exist/i.test(err.message ?? '');
}

// Última lectura buena, para decidir EN EL ACTO (sin otra consulta) si un "sin créditos" o un
// "mejorar plan" va al plan de la marca activa (ADR-2026-10-28-elegir-plan-de-marca). El shell
// y la sidebar leen el saldo al montar, así que al primer click ya está. Un error no la borra.
// Gana la lectura que EMPEZÓ última (`secuencia`): una vieja que termina tarde no pisa a la
// nueva, y `olvidarUltimoSaldo` (cierre de sesión) invalida también las que estén en vuelo.
let ultimo: Saldo | null = null;
let secuencia = 0;
const oyentes = new Set<() => void>();
// Con facturación por marca, un cambio de marca deja `ultimo` con la billetera de la marca
// ANTERIOR hasta que termine la próxima lectura. Se sigue mostrando (la bandera de la empresa
// no cambia), pero no sirve para decidir A QUÉ marca va un plan
// (ADR-2026-10-28-review-greptile-plan-por-marca).
let deOtraMarca = false;

export function ultimoSaldoLeido(): Saldo | null {
  return ultimo;
}

/**
 * El último saldo leído sólo si sigue siendo el de la marca activa: `null` desde un cambio de
 * marca (con facturación por marca) hasta que llega una lectura que empezó después.
 */
export function ultimoSaldoVigente(): Saldo | null {
  return deOtraMarca ? null : ultimo;
}

/**
 * Lo llama `atlas:brand-changed`. Sin bandera no hace nada (el saldo es de la empresa). Con
 * bandera, si el evento no trae la marca o trae otra, el saldo pasa a ser de otra marca y las
 * lecturas que ya estaban en vuelo (pueden haber resuelto la marca vieja) no lo reponen.
 */
export function alCambiarLaMarca(brandId?: string | null): void {
  if (!ultimo?.facturaPorMarca) return;
  if (brandId && brandId === ultimo.brandId) return;
  deOtraMarca = true;
  secuencia++;
}

if (typeof window !== 'undefined') {
  window.addEventListener('atlas:brand-changed', (e) => {
    alCambiarLaMarca((e as CustomEvent<{ brandId?: string | null } | null>).detail?.brandId);
  });
}

/** Avisa cada vez que cambia el último saldo leído. Devuelve la desuscripción. */
export function alCambiarElUltimoSaldo(fn: () => void): () => void {
  oyentes.add(fn);
  return () => {
    oyentes.delete(fn);
  };
}

function avisar(): void {
  for (const fn of [...oyentes]) fn();
}

/** Borra el último saldo leído (lo llama `limpiarEstadoDeCuenta` al cambiar de cuenta). */
export function olvidarUltimoSaldo(): void {
  secuencia++;
  deOtraMarca = false;
  if (ultimo === null) return;
  ultimo = null;
  avisar();
}

export async function leerSaldo(idCompany: string): Promise<Saldo> {
  const mia = ++secuencia;
  const s = await leerSaldoSinRecordar(idCompany);
  if (mia === secuencia) {
    ultimo = s;
    deOtraMarca = false;
    avisar();
  }
  return s;
}

/**
 * El último saldo leído (si sigue siendo el de la marca activa: `ultimoSaldoVigente`) o, si no
 * hay, una lectura del de la empresa del usuario logueado. Devuelve el valor de ESA lectura aunque otra más nueva (o un cierre de sesión) la
 * haya dejado fuera del cache. `null` sin sesión, sin empresa, si la lectura falla o si tarda
 * más que `topeMs`: los botones que deciden entre el plan de la marca y el catálogo de la
 * empresa nunca quedan esperando (ADR-2026-10-28-elegir-plan-de-marca-3).
 */
export async function asegurarSaldoLeido({ topeMs = 3000 }: { topeMs?: number } = {}): Promise<Saldo | null> {
  const vigente = ultimoSaldoVigente();
  if (vigente) return vigente;
  let t: ReturnType<typeof setTimeout> | undefined;
  const tope = new Promise<null>((res) => {
    t = setTimeout(() => res(null), topeMs);
  });
  try {
    return await Promise.race([leerSaldoDelUsuario(), tope]);
  } finally {
    clearTimeout(t);
  }
}

async function leerSaldoDelUsuario(): Promise<Saldo | null> {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return null;
    const { data } = await supabase.from('profiles').select('id_company').eq('id', user.id).maybeSingle();
    const idCompany = (data as { id_company?: string | null } | null)?.id_company;
    if (!idCompany) return null;
    return await leerSaldo(idCompany);
  } catch {
    return null;
  }
}

async function leerSaldoSinRecordar(idCompany: string): Promise<Saldo> {
  // Contrato: si no se puede leer, TIRA (nunca devuelve 0 por error). Los hooks (`useSaldo`)
  // atrapan y conservan el último saldo bueno; los demás callers deciden qué mostrar.
  // Antes de aplicar la migración la columna `facturacion_por_marca` no existe (PostgREST 42703):
  // se reintenta sin ella y la empresa se trata como sin bandera.
  const COLS = 'plan, max_fotos_mes, imagenes_disponibles';
  let r = await supabase
    .from('company_profiles')
    .select(`${COLS}, facturacion_por_marca`)
    .eq('id_company', idCompany)
    .maybeSingle();
  if (r.error && faltaLaColumna(r.error)) {
    r = await supabase.from('company_profiles').select(COLS).eq('id_company', idCompany).maybeSingle();
  }
  if (r.error) throw r.error;
  const emp = r.data;
  const e = (emp ?? null) as {
    plan?: string | null;
    max_fotos_mes?: number | null;
    imagenes_disponibles?: number | null;
    facturacion_por_marca?: boolean | null;
  } | null;

  if (!e?.facturacion_por_marca) {
    return {
      disponibles: num(e?.imagenes_disponibles),
      capacidad: e?.max_fotos_mes == null ? null : num(e.max_fotos_mes),
      plan: e?.plan ?? null,
      facturaPorMarca: false,
      brandId: null,
      sinPlanDeMarca: false,
    };
  }

  // La marca activa SIN tragarse errores: `resolveMarcaActivaId` cae a la default si falla la
  // lectura de `profiles.current_brand_id`, y con B activa se guardaba la billetera de A como
  // último saldo bueno (créditos de A en los chequeos, planes de A en los botones). Si no se
  // puede confirmar la marca, la lectura falla como cualquier otra (review Greptile #210,
  // ADR-2026-10-28-review-greptile-plan-por-marca-2). `{ id: null }` = la empresa no tiene marcas.
  const activa = await resolveMarcaActivaEstricta(idCompany);
  if (!activa) throw new Error('No se pudo confirmar la marca activa para leer su saldo.');
  const brandId = activa.id;
  type Fila = { plan?: string | null; max_fotos_mes?: number | null; imagenes_disponibles?: number | null };
  let fila = null as Fila | null;
  if (brandId) {
    const { data, error } = await supabase
      .from('billeteras_de_marca')
      .select('plan, max_fotos_mes, imagenes_disponibles')
      .eq('brand_id', brandId)
      .maybeSingle();
    if (error) throw error;
    fila = (data ?? null) as Fila | null;
  }
  return {
    disponibles: num(fila?.imagenes_disponibles),
    capacidad: fila?.max_fotos_mes == null ? null : num(fila.max_fotos_mes),
    plan: fila?.plan ?? null,
    facturaPorMarca: true,
    brandId: brandId ?? null,
    sinPlanDeMarca: !fila || fila.plan == null,
  };
}

/** Plan y saldo de la billetera de una marca, para el selector de marcas y su plan. */
export interface BilleteraDeMarca {
  brandId: string;
  plan: string | null;
  disponibles: number;
  capacidad: number | null;
  status: string | null;
  cobroRechazadoAt: string | null;
  fechaCancelacionProgramada: string | null;
}

const COLS_BILLETERA =
  'brand_id, plan, max_fotos_mes, imagenes_disponibles, status, cobro_rechazado_at, fecha_cancelacion_programada';

type FilaBilletera = {
  brand_id: string;
  plan?: string | null;
  max_fotos_mes?: number | null;
  imagenes_disponibles?: number | null;
  status?: string | null;
  cobro_rechazado_at?: string | null;
  fecha_cancelacion_programada?: string | null;
};

const aBilletera = (f: FilaBilletera): BilleteraDeMarca => ({
  brandId: f.brand_id,
  plan: f.plan ?? null,
  disponibles: num(f.imagenes_disponibles),
  capacidad: f.max_fotos_mes == null ? null : num(f.max_fotos_mes),
  status: f.status ?? null,
  cobroRechazadoAt: f.cobro_rechazado_at ?? null,
  fechaCancelacionProgramada: f.fecha_cancelacion_programada ?? null,
});

/**
 * Las billeteras de varias marcas (sólo facturación por marca). La lee cualquiera de la
 * empresa: el RLS de `billeteras_de_marca` deja ver las de las marcas permitidas; las que no
 * tienen fila no vienen (= sin plan). Tira ante un error de lectura.
 */
export async function leerBilleterasDeMarcas(brandIds: string[]): Promise<Map<string, BilleteraDeMarca>> {
  const r = new Map<string, BilleteraDeMarca>();
  if (brandIds.length === 0) return r;
  const { data, error } = await supabase.from('billeteras_de_marca').select(COLS_BILLETERA).in('brand_id', brandIds);
  if (error) throw error;
  for (const f of (data ?? []) as FilaBilletera[]) r.set(f.brand_id, aBilletera(f));
  return r;
}

/**
 * Espera a que el webhook de pago deje la billetera de la marca con `plan` (el alta y el
 * cambio de nivel los aplica SÓLO el webhook). `true` apenas se ve; `false` si no llegó en
 * los intentos. Un error de lectura cuenta como "todavía no".
 */
export async function esperarPlanDeMarca(
  brandId: string,
  plan: string,
  { intentos = 20, cadaMs = 1500 }: { intentos?: number; cadaMs?: number } = {},
): Promise<boolean> {
  for (let i = 0; i < intentos; i++) {
    if (i > 0) await new Promise((res) => setTimeout(res, cadaMs));
    const { data, error } = await supabase
      .from('billeteras_de_marca')
      .select('plan, imagenes_disponibles')
      .eq('brand_id', brandId)
      .maybeSingle();
    if (!error && (data as { plan?: string | null } | null)?.plan === plan) return true;
  }
  return false;
}

/** Créditos disponibles de una marca según `/marca/estado` (el único lugar que lee ese campo). */
export function disponiblesDeEstadoDeMarca(e: { imagenes_disponibles: unknown } | undefined): number {
  return Math.max(0, Math.floor(num(e?.imagenes_disponibles)));
}
